import { createContext, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useUnitSystemPreferences } from "@RequestFormV2/hooks/useUnitSystemPreferences";
import { usePackingListData } from "@RequestFormV2/sections/PackingList/context/usePackingListData";
import { usePackingListHandlers } from "@RequestFormV2/sections/PackingList/context/usePackingListHandlers";
import { usePackingListValidation } from "@RequestFormV2/sections/PackingList/context/usePackingListValidation";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { splitPackingListRows } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

type PackingListContextType = ReturnType<typeof usePackingListData> &
  ReturnType<typeof usePackingListHandlers> &
  ReturnType<typeof usePackingListValidation> &
  ReturnType<typeof splitPackingListRows>;

const PackingListContext = createContext<PackingListContextType | null>(null);

function PackingListProvider({ children }: { children: React.ReactNode }) {
  const data = usePackingListData();
  const { weightUnit, dimensionUnit } = useUnitSystemPreferences();
  const unitDefaults = useMemo(
    () => ({ m_unit: dimensionUnit, w_unit: weightUnit }),
    [dimensionUnit, weightUnit],
  );
  const handlers = usePackingListHandlers({ defaultCurrency: data.defaultCurrency, unitDefaults });
  const validation = usePackingListValidation();
  const packingList = useWatch({ name: "packing_list" }) as PackingListRow[] | undefined;

  const { containerRows, handlingUnitRows } = useMemo(
    () => splitPackingListRows(packingList, handlers.packingListFields),
    [packingList, handlers.packingListFields],
  );

  const value = useMemo(
    () => ({ ...data, ...handlers, ...validation, containerRows, handlingUnitRows }),
    [data, handlers, validation, containerRows, handlingUnitRows],
  );

  return <PackingListContext.Provider value={value}>{children}</PackingListContext.Provider>;
}

export { PackingListContext, PackingListProvider };
